import { Button, Drawer, Input, Select, Textarea, toast } from '@pcrm/fe/ui-kit';
import { DOCUMENT_FILE_ACCEPT, MAX_DOCUMENT_FILE_BYTES } from '@pcrm/shared/files';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Field } from '../../components/field';
import { ApiError } from '../../lib/api';
import { nomenclatorQueryOptions } from '../settings/settings.api';
import {
  cancelDocument,
  canTerminate,
  DOCUMENTS_QUERY_KEY,
  type DocumentListItem,
  terminateDocument,
} from './documents.api';
import { fileProblem } from './upload-document-form';

/**
 * **Sertarul de motiv**: anularea unui act nesemnat, rezilierea unuia semnat.
 *
 * Sertar și nu un dialog de confirmare: „Ești sigur?" n-are unde să primească un motiv.
 *
 * ## Un sertar, două acțiuni
 *
 * Tichetul [#140](https://github.com/bplugaru/efx-pcrm/issues/140) o cere explicit — *„se
 * numește după ce e: «Reziliază» pe un act semnat, «Anulează» pe unul nesemnat — același
 * sertar de motiv"*. De aceea componenta și-a păstrat numele: **e un singur drum**, cu un
 * cuvânt care se schimbă după stare. Două sertare ar fi însemnat două formulare cu aceleași
 * câmpuri, care diverg la prima corectură.
 *
 * Ce se deosebește, și numai atât:
 *
 * | | anulare (act **emis** nesemnat) | reziliere (`semnat`) |
 * |---|---|---|
 * | motiv | **obligatoriu** | opțional, **dacă** vine cu act de încetare |
 * | act de încetare | — | fișier + tipul lui, opționale |
 * | ce rămâne pe act | numărul consumat, actul mort | fișierul semnat, neatins |
 *
 * ⚠️ **Sertarul nu explică ce se vede** (fondatorul, 2026-09-18: *„este useless"*, despre
 * rândul care spunea că actul semnat nu se șterge și nu se rescrie). Ce trebuia scris acolo e
 * scris aici, în cod, și în wiki — unde se caută. Vezi contradicția 29.
 *
 * ⚠️ **Motivul devine opțional doar la reziliere, și doar cu fișier** (fondatorul,
 * 2026-09-18: *„trebuie să poată pune un motiv. sau să pună un document"*). Butonul rămâne
 * stins cât lipsesc amândouă — un act ieșit din vigoare fără nicio urmă de „de ce" e exact
 * rândul de registru care nu mai răspunde la nimic peste un an. Regula care contează e pe
 * server (`DocumentTerminationEvidenceMissingError`); aici doar nu se lasă apăsat degeaba.
 *
 * ⚠️ **Actul de încetare urcat e un document cu numărul lui**, nu un atașament: intră în
 * registru ca orice act primit, iar contractul reziliat arată spre el. De aceea i se cere
 * tipul — nomenclatorul e deschis, iar un cod cablat ar fi cerut un rând de seed în fiecare
 * bază (fondatorul, 2026-08-18).
 *
 * ⚠️ **O ciornă nu ajunge niciodată aici**
 * ([#142](https://github.com/bplugaru/efx-pcrm/issues/142)). Ce n-are număr n-a fost emis,
 * deci n-are ce anula — se **șterge**, cu `useDeleteDocumentPrompt`, și de acolo vine și
 * deosebirea de formă: ștergerea nu cere motiv, deci e un `Prompt`, nu un sertar. Ce mai
 * ajunge pe ramura de **anulare** sunt actele **primite** nesemnate, care iau număr la
 * încărcare.
 */
export function CancelDocumentDrawer({
  document,
  onClose,
}: {
  document: DocumentListItem | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const documentTypes = useQuery(nomenclatorQueryOptions('tipuri-document'));
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (document) {
      setReason('');
      setFile(null);
      setDocumentTypeId('');
      setSubmitted(false);
    }
  }, [document]);

  /**
   * Rezilierea, nu anularea? **Se citește starea actului**, nu o prop a ecranului.
   *
   * Ecranele care deschid sertarul (registrul, tabul clientului, actele partenerului) au
   * toate același meniu, iar acela a decis deja care acțiune se oferă — `canTerminate` e
   * aceeași funcție. Un flag transmis de fiecare ecran ar fi fost a doua copie a regulii,
   * care se uită exact la al treilea ecran.
   */
  const terminating = document !== null && canTerminate(document.documentState);
  // Doar tipurile în uz: unul dezactivat rămâne pe actele emise cu el, dar serverul refuză să
  // mai primească altele (`document_type_inactive`).
  const availableTypes = (documentTypes.data ?? []).filter((type) => type.status);
  const problem = fileProblem(file);
  const hasReason = reason.trim().length > 0;
  const canSubmit = terminating ? hasReason || file !== null : hasReason;

  const mutation = useMutation({
    mutationFn: () => {
      if (!document) {
        throw new Error('Niciun document de anulat.');
      }

      if (!terminating) {
        return cancelDocument(document.id, reason);
      }

      return terminateDocument({
        id: document.id,
        reason,
        ...(file === null ? {} : { file, documentTypeId }),
      });
    },
    onSuccess: async () => {
      toast.success(terminating ? 'Documentul a fost reziliat' : 'Documentul a fost anulat', {
        // Ce **nu** s-a întâmplat, la reziliere: prima grijă a operatorului e dovada. Actul
        // semnat rămâne descărcabil din același meniu, iar abonamentele nu s-au atins —
        // altfel cineva ar reintra pe fișa clientului să verifice, de fiecare dată.
        description: terminating
          ? 'Actul semnat rămâne în registru și se deschide în continuare. Abonamentele nu s-au schimbat.'
          : undefined,
      });
      // Toate listele de documente, nu doar cea din care s-a apăsat: același document se
      // vede și pe fișa clientului, și în „Documente emise" — iar rezilierea cu fișier mai
      // naște un rând, actul de încetare.
      await queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError
          ? error.message
          : terminating
            ? 'Documentul nu a putut fi reziliat.'
            : 'Documentul nu a putut fi anulat.',
      );
    },
  });

  return (
    <Drawer open={document !== null} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>
            {document
              ? // Numărul e mereu acolo pe drumul ăsta — se anulează doar acte emise, se reziliază
                // doar acte semnate (#142). Condiția e a tipului coloanei, nu a unui caz real.
                `${terminating ? 'Reziliază' : 'Anulează'} ${document.documentTypeName}${
                  document.documentNumber === null ? '' : ` nr. ${document.documentNumber}`
                }`
              : ''}
          </Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-4">
          <Field label="Motiv" id="anulare-motiv" required={!terminating}>
            <Textarea
              id="anulare-motiv"
              rows={3}
              maxLength={255}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              aria-required={!terminating}
              disabled={mutation.isPending}
            />
          </Field>

          {terminating ? (
            <>
              <Field
                label="Act de încetare"
                id="reziliere-fisier"
                error={problem}
                hint={`Opțional. PDF sau poză, cel mult ${Math.round(MAX_DOCUMENT_FILE_BYTES / (1024 * 1024))} MB.`}
              >
                <Input
                  id="reziliere-fisier"
                  type="file"
                  // `accept` e o comoditate — filtrează ce vede operatorul în dialogul de
                  // fișiere. Ce contează se verifică pe server, pe conținutul primit.
                  accept={DOCUMENT_FILE_ACCEPT}
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                  disabled={mutation.isPending}
                />
              </Field>

              {/* Tipul apare **abia după ce există fișier**: fără el n-are ce numi, iar un
                  select gol într-un sertar de reziliere e o întrebare fără rost. */}
              {file === null ? null : (
                <Field
                  label="Tipul actului de încetare"
                  id="reziliere-tip"
                  required
                  error={submitted && documentTypeId === '' ? 'Alege tipul.' : undefined}
                >
                  <Select
                    value={documentTypeId}
                    onValueChange={setDocumentTypeId}
                    disabled={mutation.isPending}
                  >
                    <Select.Trigger id="reziliere-tip">
                      <Select.Value placeholder="Alege tipul" />
                    </Select.Trigger>
                    <Select.Content className="z-50">
                      {availableTypes.map((type) => (
                        <Select.Item key={type.id} value={type.id}>
                          {type.name}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                </Field>
              )}
            </>
          ) : null}
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Renunță
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setSubmitted(true);

              // Tipul e obligatoriu **doar cu fișier**, iar fără el cererea ar fi plecat ca să
              // se întoarcă cu `document_termination_type_missing`: un drum dus-întors de
              // câțiva megabytes pentru un câmp neatins.
              if (file !== null && (documentTypeId === '' || problem !== undefined)) {
                return;
              }

              mutation.mutate();
            }}
            isLoading={mutation.isPending}
            disabled={mutation.isPending || !canSubmit}
          >
            {terminating ? 'Reziliază documentul' : 'Anulează documentul'}
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}
